import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BookOpen, ThumbsUp, ThumbsDown, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { downloadAuthedFile } from '../../lib/fileDownload';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Humanize a section slug → readable label (e.g. "4-the-portfolio-view" → "The Portfolio View")
const labelFor = (slug) => {
  if (slug === 'overall') return 'Overall (whole guide)';
  return slug
    .replace(/^\d+-/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const pctColor = (pct) =>
  pct == null ? 'text-slate-400' : pct >= 80 ? 'text-emerald-700' : pct >= 50 ? 'text-amber-700' : 'text-rose-700';

function SectionRow({ s }) {
  const [open, setOpen] = useState(false);
  const hasNo = s.no_voters.length > 0;
  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`guide-feedback-section-${s.section}`}>
      <button
        type="button"
        onClick={() => hasNo && setOpen((o) => !o)}
        className={`w-full flex items-center gap-3 py-2 text-left ${hasNo ? 'cursor-pointer' : 'cursor-default'}`}
      >
        {hasNo ? (
          open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <span className="flex-1 min-w-0 text-xs font-medium text-[#0F2040] truncate">{labelFor(s.section)}</span>
        <span className={`text-xs font-bold tabular-nums w-10 text-right ${pctColor(s.helpful_pct)}`}>
          {s.helpful_pct == null ? '—' : `${s.helpful_pct}%`}
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 w-8 justify-end">
          <ThumbsUp className="w-3 h-3" /> {s.yes}
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-rose-600 w-8 justify-end">
          <ThumbsDown className="w-3 h-3" /> {s.no}
        </span>
      </button>
      {open && hasNo && (
        <ul className="pl-7 pb-2 space-y-1" data-testid={`guide-feedback-section-${s.section}-voters`}>
          {s.no_voters.map((v, i) => (
            <li key={`${v.email || v.name}-${i}`} className="text-[11px]" data-testid="guide-feedback-no-voter">
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate">
                  <span className="font-medium text-[#0F2040]">{v.name}</span>
                  {v.email && <span className="text-slate-400 ml-1.5">{v.email}</span>}
                </span>
                {v.email && (
                  <a href={`mailto:${v.email}`} className="shrink-0 text-[#4ABFCF] hover:underline" title={`Email ${v.name}`}>
                    {v.voted_at ? new Date(v.voted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Email'}
                  </a>
                )}
              </div>
              {v.comment && (
                <p className="text-slate-500 italic mt-0.5" data-testid="guide-feedback-comment">
                  “{v.comment}”
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Admin-only tile (Verification Health page) — per-section User Guide
 * helpfulness so the team can pinpoint and rewrite the weakest topics.
 * Renders nothing for non-admins (summary endpoint 403s) or with no votes.
 */
export default function GuideFeedbackTile() {
  const [summary, setSummary] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/api/guide/feedback/summary`, { withCredentials: true })
      .then((r) => mounted && setSummary(r.data))
      .catch((e) => {
        if (mounted && e?.response?.status === 403) setDenied(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (denied || !summary || summary.total === 0) return null;

  return (
    <Card data-testid="guide-feedback-tile">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-[#4ABFCF]" />
            <h2 className="text-sm font-bold text-[#0F2040]">User Guide helpfulness</h2>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className={`text-lg font-bold tabular-nums ${pctColor(summary.helpful_pct)}`} data-testid="guide-feedback-pct">
              {summary.helpful_pct == null ? '—' : `${summary.helpful_pct}%`}
            </span>
            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs" data-testid="guide-feedback-yes-count">
              <ThumbsUp className="w-3.5 h-3.5" /> {summary.yes}
            </span>
            <span className="inline-flex items-center gap-1 text-rose-700 text-xs" data-testid="guide-feedback-no-count">
              <ThumbsDown className="w-3.5 h-3.5" /> {summary.no}
            </span>
            <button
              type="button"
              onClick={async () => {
                try {
                  await downloadAuthedFile('/api/guide/feedback/export', 'guide_feedback.csv');
                } catch {
                  toast.error('Could not export the feedback CSV.');
                }
              }}
              title="Export sections + comments as CSV"
              className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="guide-feedback-export-btn"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
          By section — weakest first · expand to see who to follow up
        </div>
        <div className="max-h-72 overflow-y-auto" data-testid="guide-feedback-sections">
          {summary.sections.map((s) => (
            <SectionRow key={s.section} s={s} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
